/**
 * Google Maps API Loader
 *
 * Utility to dynamically load the Google Maps JavaScript API.
 * Uses the official @googlemaps/js-api-loader package (v2.x functional API).
 *
 * IMPORTANT: Only used for PROVIDER/CLINIC locations.
 * Do NOT use for patient addresses (PHI concerns).
 */

import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
// NOTE: Types come from @types/google.maps - no local types import needed

// Window with Google Maps types
type WindowWithGoogle = Window & typeof globalThis & { google?: typeof google };

let loadPromise: Promise<typeof google | null> | null = null;
let optionsSet = false;
const LOAD_TIMEOUT_MS = 10000;

/** Gets Google Maps API key from env. Returns empty string if not configured. @agentic connections */
function getApiKey(): string {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.warn(
      "[GoogleMaps] VITE_GOOGLE_MAPS_API_KEY not set. Places autocomplete will be disabled.",
    );
    return "";
  }
  return apiKey;
}

/**
 * Dynamically load the Google Maps JavaScript API (places and geocoding libraries).
 * @agentic connections
 */
export async function loadGoogleMapsApi(): Promise<typeof google | null> {
  const apiKey = getApiKey();
  if (!apiKey) {
    return null;
  }

  const win = window as WindowWithGoogle;

  // Set options only once. Note: if setOptions was already called elsewhere
  // (e.g., HMR, cached state), this may warn but that's okay - we'll still
  // import the libraries we need below via importLibrary().
  if (!optionsSet) {
    try {
      setOptions({
        key: apiKey,
        v: "weekly",
        // Request all libraries we might need
        libraries: ["places", "geocoding"],
      });
    } catch {
      // setOptions may throw if already called - ignore
    }
    optionsSet = true;
  }

  // Check if already loaded successfully (including geocoding)
  if (
    loadPromise &&
    typeof win.google !== "undefined" &&
    win.google.maps?.places &&
    win.google.maps?.Geocoder
  ) {
    return win.google;
  }

  if (!loadPromise) {
    // Import libraries individually - this works even if setOptions was called
    // with different libraries, as importLibrary can load any library on demand.
    loadPromise = (async () => {
      // Load places first (most commonly used)
      await importLibrary("places");
      // Load geocoding - this will work regardless of what setOptions specified
      await importLibrary("geocoding");
      return (window as WindowWithGoogle).google || null;
    })();
  }

  try {
    const result = await Promise.race([
      loadPromise,
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), LOAD_TIMEOUT_MS);
      }),
    ]);
    if (!result) {
      console.warn("[GoogleMaps] API load timed out.");
      loadPromise = null;
      return null;
    }
    return result;
  } catch (error) {
    console.error("[GoogleMaps] Failed to load API:", error);
    loadPromise = null;
    return null;
  }
}
